/**
 * REFRESH-MULTITAB-1 (#1558) — one session renewal across all tabs.
 *
 * Every tab keeps its tokens in memory and may send the refresh token in the
 * request body (the cookie-blocked fallback). Before this, a tab whose memory
 * token had been rotated by another tab presented a stale token; the server
 * treats that as reuse and revokes the whole session family, signing every
 * tab out.
 *
 *   1. One renewal at a time across tabs: navigator.locks('zephix-refresh'),
 *      or a BroadcastChannel leader when locks are unavailable.
 *   2. The renewing tab broadcasts the new tokens BEFORE it releases the lock.
 *      Tokens stay in memory only — never localStorage / sessionStorage.
 *   3. A tab that waited first re-checks the session with its current
 *      (possibly just-broadcast) tokens and renews only if that fails.
 *   4. The body token stays as the fallback; with 1–3 it is always current.
 */

export type SessionTokens = { access: string | null; refresh: string | null };

export interface CrossTabRefreshDeps {
  /** This tab's in-memory tokens. */
  getTokens(): SessionTokens;
  /** Replace this tab's in-memory tokens. */
  setTokens(tokens: SessionTokens): void;
  /** POST /auth/refresh with this tab's current tokens; applies the result to memory. */
  renew(): Promise<void>;
  /** True when the current tokens still authenticate (GET /auth/me). */
  sessionIsValid(): Promise<boolean>;
  /**
   * Another tab renewed as a DIFFERENT user/org (an account switch). This
   * tab's tokens are already cleared; it must not keep showing the old user.
   */
  onAccountSwitch?(): void;
}

export type TokenIdentity = { userId: string; orgId: string | null };

/**
 * Who a JWT belongs to (`sub` + `organizationId`). Decoded, NOT verified —
 * used only to refuse adopting another account's tokens, never to grant.
 */
export function tokenIdentity(token: string | null): TokenIdentity | null {
  if (!token) return null;
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as {
      sub?: unknown;
      organizationId?: unknown;
    };
    if (typeof json.sub !== 'string' || !json.sub) return null;
    return { userId: json.sub, orgId: typeof json.organizationId === 'string' ? json.organizationId : null };
  } catch {
    return null;
  }
}

type Message =
  | {
      type: 'tokens';
      from: string;
      access: string | null;
      refresh: string | null;
      userId: string | null;
      orgId: string | null;
    }
  | { type: 'claim'; from: string; at: number }
  | { type: 'renewing'; from: string }
  | { type: 'done'; from: string; ok: boolean };

export const REFRESH_LOCK_NAME = 'zephix-refresh';
export const REFRESH_CHANNEL_NAME = 'zephix-refresh';

/** How long a waiting tab gives the winner's token message to arrive. */
const TOKENS_GRACE_MS = 300;
/** BroadcastChannel fallback: how long claims are collected before electing. */
const CLAIM_WINDOW_MS = 60;
/** BroadcastChannel fallback: how long a loser waits for the leader. */
const LEADER_TIMEOUT_MS = 10_000;

type LockManagerLike = {
  request<T>(
    name: string,
    optionsOrCb: { ifAvailable?: boolean } | ((lock: unknown) => Promise<T>),
    cb?: (lock: unknown) => Promise<T>,
  ): Promise<T>;
};

function defaultLocks(): LockManagerLike | null {
  const nav = typeof navigator !== 'undefined' ? (navigator as { locks?: LockManagerLike }) : undefined;
  return nav?.locks && typeof nav.locks.request === 'function' ? nav.locks : null;
}

function newTabId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  return c?.randomUUID ? c.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export interface CrossTabRefresh {
  /** Renew once across tabs, or adopt another tab's renewal. */
  renew(): Promise<void>;
  /** Stop listening (tests). */
  close(): void;
}

export function createCrossTabRefresh(
  deps: CrossTabRefreshDeps,
  env: {
    locks?: LockManagerLike | null;
    channel?: BroadcastChannel | null;
    tabId?: string;
  } = {},
): CrossTabRefresh {
  const tabId = env.tabId ?? newTabId();
  const locks = env.locks === undefined ? defaultLocks() : env.locks;
  const channel =
    env.channel !== undefined
      ? env.channel
      : typeof BroadcastChannel !== 'undefined'
        ? new BroadcastChannel(REFRESH_CHANNEL_NAME)
        : null;

  let tokensSeen = 0;
  let tokenWaiters: Array<() => void> = [];
  let claims: Array<{ from: string; at: number }> = [];
  let otherRenewing = false;
  let otherRenewingTimer: ReturnType<typeof setTimeout> | null = null;
  let leaderWaiters: Array<(ok: boolean) => void> = [];

  function onMessage(msg: Message): void {
    if (!msg || typeof msg !== 'object' || msg.from === tabId) return;
    if (msg.type === 'tokens') {
      // A signed-out tab does not adopt another tab's session.
      const mine = deps.getTokens();
      if (mine.access || mine.refresh) {
        // Adopt ONLY the same user + org. Identity unknown on either side →
        // adopt nothing. A different account → an account switch: clear and
        // let the host leave this screen; never act as the other user.
        const own = tokenIdentity(mine.access);
        const theirs = msg.userId ? { userId: msg.userId, orgId: msg.orgId ?? null } : null;
        if (own && theirs) {
          if (own.userId === theirs.userId && own.orgId === theirs.orgId) {
            deps.setTokens({ access: msg.access, refresh: msg.refresh });
          } else {
            deps.setTokens({ access: null, refresh: null });
            deps.onAccountSwitch?.();
          }
        }
      }
      tokensSeen += 1;
      const w = tokenWaiters;
      tokenWaiters = [];
      w.forEach((fn) => fn());
    } else if (msg.type === 'claim') {
      // Receipt time: a claim that arrived just before this tab starts its own
      // election still counts (otherwise two tabs can each elect themselves).
      claims.push({ from: msg.from, at: Date.now() });
    } else if (msg.type === 'renewing') {
      otherRenewing = true;
      // A leader that closes mid-renewal never sends 'done'.
      if (otherRenewingTimer) clearTimeout(otherRenewingTimer);
      otherRenewingTimer = setTimeout(() => {
        otherRenewing = false;
      }, LEADER_TIMEOUT_MS);
    } else if (msg.type === 'done') {
      otherRenewing = false;
      if (otherRenewingTimer) clearTimeout(otherRenewingTimer);
      const w = leaderWaiters;
      leaderWaiters = [];
      w.forEach((fn) => fn(msg.ok));
    }
  }

  if (channel) {
    channel.onmessage = (ev: MessageEvent) => onMessage(ev.data as Message);
    // Node's BroadcastChannel keeps the process alive; browsers have no unref.
    (channel as { unref?: () => void }).unref?.();
  }

  function post(msg: Message): void {
    channel?.postMessage(msg);
  }

  async function renewAndBroadcast(): Promise<void> {
    await deps.renew();
    const t = deps.getTokens();
    const who = tokenIdentity(t.access);
    // Step 2: broadcast BEFORE the caller releases the lock, with whose tokens they are.
    post({
      type: 'tokens',
      from: tabId,
      access: t.access,
      refresh: t.refresh,
      userId: who?.userId ?? null,
      orgId: who?.orgId ?? null,
    });
  }

  function waitForTokens(since: number, ms: number): Promise<void> {
    if (tokensSeen !== since) return Promise.resolve();
    return new Promise((resolve) => {
      const timer = setTimeout(resolve, ms);
      tokenWaiters.push(() => {
        clearTimeout(timer);
        resolve();
      });
    });
  }

  /** Step 3: a tab that waited adopts the winner's renewal when it can. */
  async function afterWaiting(seenBefore: number): Promise<void> {
    await waitForTokens(seenBefore, TOKENS_GRACE_MS);
    let valid = false;
    try {
      valid = await deps.sessionIsValid();
    } catch {
      valid = false;
    }
    if (valid) return;
    await renewAndBroadcast();
  }

  async function renewWithLocks(l: LockManagerLike): Promise<void> {
    const seenBefore = tokensSeen;
    const ranImmediately = await l.request(REFRESH_LOCK_NAME, { ifAvailable: true }, async (lock) => {
      if (!lock) return false;
      // The previous holder broadcast before releasing, but that message can
      // still be in flight. Let it land; if it did, adopt instead of renewing
      // with a token that was just rotated.
      await new Promise((r) => setTimeout(r, 0));
      if (tokensSeen !== seenBefore) await afterWaiting(seenBefore);
      else await renewAndBroadcast();
      return true;
    });
    if (ranImmediately) return;
    await l.request(REFRESH_LOCK_NAME, async () => {
      await afterWaiting(seenBefore);
    });
  }

  function waitForLeader(): Promise<boolean> {
    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve(false), LEADER_TIMEOUT_MS);
      leaderWaiters.push((ok) => {
        clearTimeout(timer);
        resolve(ok);
      });
    });
  }

  async function renewWithLeader(): Promise<void> {
    const seenBefore = tokensSeen;
    if (otherRenewing) {
      await waitForLeader();
      await afterWaiting(seenBefore);
      return;
    }
    const mine = { from: tabId, at: Date.now() };
    // Keep peers' recent claims (one may have arrived just before ours).
    claims = claims.filter((c) => mine.at - c.at < CLAIM_WINDOW_MS * 2);
    claims.push(mine);
    post({ type: 'claim', ...mine });
    await new Promise((r) => setTimeout(r, CLAIM_WINDOW_MS));
    // Lowest random tab id wins: every tab holding the same claims elects the same leader.
    const winner = claims.reduce((a, b) => (b.from < a.from ? b : a));
    claims = [];
    if (tokensSeen !== seenBefore) {
      // Another tab renewed while we were claiming: adopt its tokens.
      await afterWaiting(seenBefore);
      return;
    }
    if (winner.from !== tabId || otherRenewing) {
      await waitForLeader();
      await afterWaiting(seenBefore);
      return;
    }
    post({ type: 'renewing', from: tabId });
    let ok = false;
    try {
      await renewAndBroadcast();
      ok = true;
    } finally {
      post({ type: 'done', from: tabId, ok });
    }
  }

  return {
    async renew() {
      if (locks) return renewWithLocks(locks);
      if (channel) return renewWithLeader();
      return deps.renew();
    },
    close() {
      if (channel) {
        channel.onmessage = null;
        if (env.channel === undefined) channel.close();
      }
    },
  };
}
